import {
  AuthService,
  LoginPayload,
  RegisterPayload,
  AuthResult,
  User,
  CheckIdentifierResult,
  SendOtpOptions,
  SendVerificationOptions,
  MobileIdStatusResult,
} from "./authService";
import { requestManager } from "../utils/requestManager";
import { createUiErrors } from "./authErrorMapper";
import { getAbVariant, isSkipPhoneVerification } from "../utils/metrika";

// Helper function to get API base URL from environment or global config
const getApiBaseUrl = (): string => {
  // First check global configuration
  if (
    typeof window !== "undefined" &&
    (window as any).SkAuthConfig?.apiBasePath
  ) {
    return (window as any).SkAuthConfig.apiBasePath;
  }
  // Fallback to environment variable
  return import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";
};

// Detect Bitrix/global config mode
const hasGlobalSkAuthConfig = (): boolean => {
  return typeof window !== "undefined" && !!(window as any).SkAuthConfig;
};

// Add .php to endpoint only when SkAuthConfig exists
const normalizeEndpoint = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;

  if (hasGlobalSkAuthConfig()) {
    return `/api/${cleanEndpoint}.php`;
  }

  return `/api/auth/${cleanEndpoint}`;
};

interface ApiErrorResponse {
  success: false;
  errors?: string[];
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

type ApiSuccessResponse<T = Record<string, unknown>> = {
  success: true;
} & T;

type ApiResponse<T = Record<string, unknown>> =
  | ApiSuccessResponse<T>
  | ApiErrorResponse;

const apiRequest = async <T = Record<string, unknown>>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> => {
  const baseUrl = getApiBaseUrl();
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const url = `${baseUrl}${normalizedEndpoint}`;

  let response: Response;

  try {
    response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      credentials: "include",
      ...options,
    });
  } catch (fetchError) {
    console.error(`Network error fetching ${url}:`, fetchError);
    throw new Error("Ошибка сети. Проверьте подключение к интернету.");
  }

  let responseData: unknown;
  try {
    responseData = await response.json();
  } catch (parseError) {
    console.error(
      `Invalid JSON response from ${url} (${response.status}):`,
      parseError,
    );
    if (!response.ok) {
      throw new Error(
        `Произошла непредвиденная ошибка (${response.status} ${response.statusText})`,
      );
    }
    throw new Error(
      "Произошла непредвиденная ошибка при обработке ответа сервера",
    );
  }

  if (typeof responseData !== "object" || responseData === null) {
    console.error(`Invalid response format from ${url}:`, responseData);
    throw new Error("Произошла непредвиденная ошибка: неверный формат ответа");
  }

  const apiResponse = responseData as ApiResponse<T>;

  if (!response.ok) {
    if (apiResponse.success === false) {
      return apiResponse;
    }

    console.error(
      `Unexpected error response from ${url} (${response.status}):`,
      apiResponse,
    );
    throw new Error(`Произошла непредвиденная ошибка (${response.status})`);
  }

  return apiResponse;
};

// Helper to normalize auth errors from API responses
const normalizeAuthError = (
  error: unknown,
  apiResponse?: ApiErrorResponse,
): AuthResult => {
  // If we have a valid API error response with business error
  if (apiResponse && apiResponse.success === false) {
    // Use error mapper to create user-friendly messages
    const errors = createUiErrors(apiResponse.code, apiResponse.errors);
    return {
      success: false,
      errors,
    };
  }

  // If error is an Error object (network or unexpected error)
  if (error instanceof Error) {
    return {
      success: false,
      errors: [error.message],
    };
  }

  // Fallback for unknown error types
  console.error("Unknown error type:", error);
  return {
    success: false,
    errors: ["Произошла непредвиденная ошибка"],
  };
};

export class ApiAuthService implements AuthService {
  async login(payload: LoginPayload): Promise<AuthResult> {
    try {
      const data = await apiRequest<{ user?: User; sessionId?: string }>(
        "/login",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );

      if (data.success) {
        return {
          success: true,
          user: data.user,
          errors: undefined,
        };
      }

      return normalizeAuthError(null, data);
    } catch (error) {
      console.error("Login API error:", error);
      return normalizeAuthError(error);
    }
  }

  async register(payload: RegisterPayload): Promise<AuthResult> {
    try {
      const data = await apiRequest<{ user?: User; sessionId?: string }>(
        "/register",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );

      if (data.success) {
        return {
          success: true,
          user: data.user,
          errors: undefined,
        };
      }

      return normalizeAuthError(null, data);
    } catch (error) {
      console.error("Register API error:", error);
      return normalizeAuthError(error);
    }
  }

  async resetPassword(email: string): Promise<AuthResult> {
    try {
      const data = await apiRequest("/reset-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });

      if (data.success) {
        return {
          success: true,
          errors: undefined,
        };
      }

      return normalizeAuthError(null, data);
    } catch (error) {
      console.error("Reset password API error:", error);
      return normalizeAuthError(error);
    }
  }

  async requestPasswordReset(email: string): Promise<AuthResult> {
    try {
      const data = await apiRequest("/request-password-reset", {
        method: "POST",
        body: JSON.stringify({ email }),
      });

      if (data.success) {
        return {
          success: true,
          errors: undefined,
        };
      }

      return normalizeAuthError(null, data);
    } catch (error) {
      console.error("Request password reset API error:", error);
      return normalizeAuthError(error);
    }
  }

  async resetPasswordWithToken(
    token: string,
    newPassword: string,
  ): Promise<AuthResult> {
    console.log(
      `[Mock] Password reset with token: ${token}, new password: ${newPassword}`,
    );
    return {
      success: true,
    };
  }

  async sendOtp(phone: string, options?: SendOtpOptions): Promise<AuthResult> {
    return this.sendVerification(phone, {
      method: "sms",
      force: options?.force,
    });
  }

  async sendVerification(
    phone: string,
    options: SendVerificationOptions,
  ): Promise<AuthResult> {
    const force = options?.force || false;
    const key = `sendVerification:${phone}:${options.method}`;

    try {
      const data = await requestManager.execute(
        key,
        () =>
          apiRequest<{ devCode?: string; authReqId?: string }>(
            "/send-verification",
            {
              method: "POST",
              body: JSON.stringify({
                phone,
                method: options.method,
                abVariant: getAbVariant(),
                skipPhoneVerification: isSkipPhoneVerification(),
              }),
            },
          ),
        force,
      );

      if (data.success) {
        return {
          success: true,
          authReqId: data.authReqId,
          errors: undefined,
        };
      }

      return normalizeAuthError(null, data);
    } catch (error) {
      console.error("Send verification API error:", error);
      return normalizeAuthError(error);
    }
  }

  async checkMobileIdStatus(authReqId: string): Promise<MobileIdStatusResult> {
    try {
      const data = await apiRequest<{
        status?:
          | "pending"
          | "awaiting_sms_otp"
          | "success"
          | "failed"
          | "expired";
        user?: User;
        isNewUser?: boolean;
      }>("/check-mobile-id-status", {
        method: "POST",
        body: JSON.stringify({ authReqId }),
      });

      if (data.success) {
        return {
          success: true,
          status: data.status,
          user: data.user,
          isNewUser: data.isNewUser,
          errors: undefined,
        };
      }

      return normalizeAuthError(null, data);
    } catch (error) {
      console.error("Check Mobile ID status API error:", error);
      return normalizeAuthError(error);
    }
  }

  async verifyOtp(phone: string, code: string): Promise<AuthResult> {
    try {
      const data = await apiRequest<{ user?: User; sessionId?: string }>(
        "/verify-otp",
        {
          method: "POST",
          body: JSON.stringify({
            phone,
            code,
            abVariant: getAbVariant(),
            skipPhoneVerification: isSkipPhoneVerification(),
          }),
        },
      );

      if (data.success) {
        return {
          success: true,
          user: data.user,
          errors: undefined,
        };
      }

      return normalizeAuthError(null, data);
    } catch (error) {
      console.error("Verify OTP API error:", error);
      return normalizeAuthError(error);
    }
  }

  async checkIdentifier(identifier: string): Promise<CheckIdentifierResult> {
    const baseUrl = getApiBaseUrl();
    const normalizedEndpoint = normalizeEndpoint("/check-identifier");
    const url = `${baseUrl}${normalizedEndpoint}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ identifier }),
      });

      if (!response.ok) {
        // For check-identifier, non-2xx means we treat as "does not exist"
        console.warn(
          `Check identifier returned ${response.status}, treating as non-existent`,
        );
        return { exists: false };
      }

      const data = await response.json();

      // Validate response format
      if (
        typeof data === "object" &&
        data !== null &&
        "exists" in data &&
        typeof data.exists === "boolean"
      ) {
        return data as { exists: boolean };
      }

      console.error("Invalid check-identifier response format:", data);
      return { exists: false };
    } catch (error) {
      console.error("Check identifier API error:", error);
      return { exists: false };
    }
  }

  async logout(): Promise<void> {
    try {
      await apiRequest("/logout", {
        method: "POST",
      });
      // Logout is best-effort, ignore result
    } catch (error) {
      console.error("Logout API error:", error);
      // Logout errors are not critical
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const data = await apiRequest<{ user?: User }>("/me");

      if (data.success && data.user) {
        return data.user;
      }

      return null;
    } catch (error) {
      console.error("Get current user API error:", error);
      return null;
    }
  }

  async saveContactDetails(
    fullName: string,
    email: string,
    orderId?: number | string | null,
  ): Promise<AuthResult> {
    try {
      const data = await apiRequest<{ user?: User }>("/save-contact-details", {
        method: "POST",
        body: JSON.stringify({ fullName, email, orderId }),
      });

      if (data.success) {
        return {
          success: true,
          user: data.user,
        };
      }

      return normalizeAuthError(null, data);
    } catch (error) {
      console.error("Save contact details API error:", error);
      return normalizeAuthError(error);
    }
  }
}

